import { useEffect, useRef } from "react";
import {
  Chart,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  LineController,
  Filler,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { getAssetUrl } from "@/lib/assetBaseUrl";

Chart.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  LineController,
  Filler,
  Tooltip
);

const FINALE_VIDEO_PATH =
  "pb/assets/video/3aGEG1IGkRn14QZcK1khpHozNNBJkov6oL2fetjP.mp4";

const CHART_DATA = {
  labels: ["M1", "M2", "M3", "M4", "M5", "M6"],
  datasets: [
    {
      data: [58, 64, 70, 78, 86, 94],
      borderColor: "#FAAC00",
      backgroundColor: "rgba(250, 172, 0, 0.18)",
      borderWidth: 2.5,
      fill: true,
      tension: 0.45,
      pointRadius: 0,
    },
  ],
};

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
  scales: {
    x: { display: false },
    y: { display: false, min: 50, max: 100 },
  },
  animation: false,
  elements: { line: { borderJoinStyle: "round" } },
};

// Capsule definitions modeled after the homepage hero capsules:
//   - solid white pill with soft shadow
//   - small accent dot indicator
//   - stacked value + small label
const CAPSULES = [
  {
    testid: "finale-cap-math",
    varietyClass: "c1",
    dotTone: "gold",
    value: "+38%",
    label: "Maths score lift",
    delayMs: 0,
  },
  {
    testid: "finale-cap-rank",
    varietyClass: "c2",
    dotTone: "mint",
    value: "Top 5",
    label: "in class rank",
    delayMs: 90,
  },
  {
    testid: "finale-cap-growth",
    varietyClass: "c3",
    dotTone: "gold",
    value: "Marks growth",
    label: "Last 6 months",
    chart: true,
    delayMs: 180,
  },
  {
    testid: "finale-cap-mastery",
    varietyClass: "c4",
    dotTone: "mint",
    value: "98%",
    label: "concept mastery",
    delayMs: 270,
  },
];

/**
 * Finale beat — replaces the standard zoom card on the 7th anchor.
 * Driven by a single `s` (0..1) prop coming from the parent timeline:
 *   - circle + video grow from 0 -> 1
 *   - capsules stagger in
 *   - finale video plays only when fully visible (s >= 0.999)
 */
export default function JourneyFinale({ s, finaleRef, isMobile = false }) {
  const videoRef = useRef(null);
  const playingRef = useRef(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const shouldPlay = isMobile ? s >= 0.08 : s >= 0.999;
    if (shouldPlay && !playingRef.current) {
      try {
        v.currentTime = 0;
        v.playbackRate = 1.0;
        const r = v.play();
        if (r && typeof r.catch === "function") r.catch(() => {});
      } catch (e) { /* noop */ }
      playingRef.current = true;
    } else if (!shouldPlay && playingRef.current) {
      try { v.pause(); } catch (e) { /* noop */ }
      playingRef.current = false;
    }
  }, [s, isMobile]);

  // A single threshold gate — once the finale overlay starts, each capsule
  // simply waits its CSS animation-delay to pop in. This avoids continuous
  // scale-driven re-renders during scroll.
  const isShown = s > 0.08;

  return (
    <div
      ref={finaleRef}
      className="sp-finale"
      data-testid="journey-finale"
      style={{ opacity: s > 0 ? 1 : 0 }}
    >
      <div
        className="sp-finale-circle"
        data-testid="finale-circle"
        style={
          isMobile
            ? {
                transform: `scale(${s})`,
                opacity: Math.min(1, 0.35 + s * 0.65),
              }
            : {
                transform: `scale(${0.55 + s * 0.45})`,
                opacity: s,
              }
        }
      >
        <video
          ref={videoRef}
          src={getAssetUrl(FINALE_VIDEO_PATH)}
          muted
          loop
          playsInline
          preload="metadata"
          webkit-playsinline="true"
          x5-playsinline="true"
          className="sp-finale-video"
        />
      </div>

      <div className="sp-finale-capsules">
        {CAPSULES.map((c) => (
          <div
            key={c.testid}
            className={`sp-finale-cap sp-finale-cap--${c.varietyClass} ${isShown ? "is-shown" : ""}`}
            data-testid={c.testid}
            style={{ "--cap-delay": `${c.delayMs}ms` }}
          >
            <span
              className={`sp-finale-cap-dot ${c.dotTone === "gold" ? "is-gold" : "is-mint"}`}
              aria-hidden="true"
            />
            {c.chart ? (
              <div className="sp-finale-cap-chart" aria-hidden="true">
                <Line data={CHART_DATA} options={CHART_OPTIONS} />
              </div>
            ) : null}
            <div className="sp-finale-cap-text">
              <span className="sp-finale-cap-value">{c.value}</span>
              <span className="sp-finale-cap-label">{c.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
